import React, { useEffect, useRef, useState } from "react";
import { Columns3, Check } from "lucide-react";

/**
 * ColumnPicker — a compact button that opens a checkbox popover for column visibility.
 * Persists selection in localStorage under `storageKey`.
 *
 * Props:
 *   columns: [{ key: string, label: string }, ...]  — canonical list; visibility applies to the `key`
 *   storageKey: string                              — localStorage key
 *   onChange: (visibleSet: Set<string>) => void
 *   testid?: string
 */
export default function ColumnPicker({ columns, storageKey, onChange, testid = "column-picker", defaultVisible = null }) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* localStorage unavailable */ }
    if (defaultVisible) return new Set(defaultVisible);
    return new Set(columns.map((c) => c.key)); // all visible by default
  });
  const rootRef = useRef(null);

  useEffect(() => { onChange(visible); }, [visible, onChange]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function toggle(key) {
    const next = new Set(visible);
    next.has(key) ? next.delete(key) : next.add(key);
    // Enforce at least one visible column
    if (next.size === 0) return;
    setVisible(next);
    try { localStorage.setItem(storageKey, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
  }

  function reset() {
    const all = defaultVisible ? new Set(defaultVisible) : new Set(columns.map((c) => c.key));
    setVisible(all);
    try { localStorage.setItem(storageKey, JSON.stringify(Array.from(all))); } catch { /* ignore */ }
  }

  const hiddenCount = columns.length - visible.size;

  return (
    <div className="relative" ref={rootRef}>
      <button
        data-testid={`${testid}-btn`}
        onClick={() => setOpen((v) => !v)}
        className={`btn-ghost flex items-center gap-1.5 !py-1.5 !px-2.5 ${hiddenCount > 0 ? "border-[var(--gold)] text-[var(--warn)]" : ""}`}
        title="Show / hide columns"
      >
        <Columns3 size={14} strokeWidth={1.5} />
        <span className="text-xs">Columns</span>
        {hiddenCount > 0 && <span className="text-[10px] font-mono-data">({visible.size}/{columns.length})</span>}
      </button>
      {open && (
        <div
          data-testid={`${testid}-menu`}
          className="absolute right-0 top-full mt-1 w-56 bg-[var(--surface)] border border-[var(--line)] rounded shadow-lg z-50 py-1"
        >
          <div className="px-3 py-2 border-b border-[var(--line)] flex items-center justify-between">
            <span className="overline">Columns</span>
            <button
              data-testid={`${testid}-reset`}
              onClick={reset}
              className="text-[10px] text-[var(--ink-2)] underline hover:text-[var(--ink)]"
            >
              Show all
            </button>
          </div>
          <div className="max-h-72 overflow-auto py-1">
            {columns.map((c) => {
              const on = visible.has(c.key);
              return (
                <button
                  key={c.key}
                  data-testid={`${testid}-opt-${c.key}`}
                  onClick={() => toggle(c.key)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-[var(--bg)] ${on ? "text-[var(--ink)]" : "text-[var(--ink-2)]"}`}
                >
                  <span className={`w-3.5 h-3.5 flex items-center justify-center border rounded ${on ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--line)]"}`}>
                    {on && <Check size={10} strokeWidth={2.5} />}
                  </span>
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
